import { makeAutoObservable } from 'mobx';
import { amountViewToValue } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/helpers/amountViewToValue';
import { GoogleTagManagerState } from 'src/domains/layouts/state/googleState/GoogleTagManagerState';
import { UsersState } from 'src/domains/players/state/UsersState';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import {
    validateAmountRequire,
    validateMaxDeposit,
    validateMinAmountNew,
} from 'src/domains/players/webview/components/ValidatorsNew';
import { Amount } from 'src_common/common/amount/Amount';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { TrpcClient } from 'src/appState/TrpcClient';
import {
    PaysafeBillingInfoSuccess,
    paysafeBillingInfoFieldsValidation,
} from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/helpers/billingInfoFiledsValidation/paysafeBillingInfoFieldsValidation';
import {
    PaymentIframeDataType,
    PaysafeError,
    PaysafeExpired,
    PaysafeInstanceType,
    PaysafeResult,
    PaysafeStage,
} from './paysafeFrame/PaysafeFrameOptions';
import { PaysafePaymentProviderType } from 'src/domains/layouts/config/features/types';
import { DepositViewType } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/depositFailure/DepositFailure';
import { CreatePaymentPaysafeType } from 'src_server/trpc/types/createPaymentPaysafe';
import { createPaymentPaysafe } from 'src_server/trpc/types';
import { TransactionCallbackType } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/depositFinalView/DepositFinalView';
import { RemainingLimitState } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/remainingLimit/RemainingLimit.state';
import {
    DepositYaspaPageType,
    YaspaUrlResponseType,
} from 'src/domains/players/webview/components/WithdrawAndDeposit/yaspa/YaspaSegment/YaspaSegment.state';
import { Logger } from 'src/domains/common/Logger';
import { BasicDataModel } from 'src/domains/players/state/BasicDataModel';
import { Common } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { PaysafePaymentProviderZod } from 'src/domains/common/configFeatures/ConfigFeatures';
import { AmountPrecision } from 'src_common/common/amount/AmountPrecision';
import {
    DepositErrorMessagesState,
    ErrorMessageType,
} from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/errorMessages/DepositErrorMessages.state';
import {
    isAccountDepositBlocked,
    refreshAccountBasicDataForDeposit,
    syncAccountDepositBlockingErrors,
} from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/helpers/accountDepositBlocking';

export interface IframeViewDataType {
    paymentIframeData: PaymentIframeDataType;
}
interface PaysafeFrameDataType {
    paymentIframeData: PaymentIframeDataType;
    paysafeDepositSteps: PaysafeDepositSteps;
}

export type DepositStepsType =
    | {
          readonly type: 'set-method';
      }
    | {
          readonly type: 'set-method-signup';
      }
    | {
          readonly type: 'iframe-view';
          paymentIframeData: PaymentIframeDataType;
          paysafeDepositSteps: PaysafeDepositSteps;
      }
    | {
          readonly type: 'iframe-view-yaspa';
          readonly url: string;
          readonly yaspaView: 'iframe-view' | 'loading-view';
      }
    | {
          readonly type: 'final-view';
          readonly transactionId: number;
      }
    | {
          readonly type: 'failure-view';
          readonly failureType: DepositViewType;
          readonly transactionCallback?: TransactionCallbackType;
          readonly longError?: ErrorMessageType[];
      };

export class PaysafeDepositSteps {
    public step: DepositStepsType;

    public constructor(private readonly isSignup: boolean) {
        this.step = {
            type: this.isSignup ? 'set-method-signup' : 'set-method',
        };
        makeAutoObservable(this);
    }

    public redirectToSetMethod = (): void => {
        this.step = {
            type: this.isSignup ? 'set-method-signup' : 'set-method',
        };
    };

    public redirectToIframeNewCard = ({ paymentIframeData, paysafeDepositSteps }: PaysafeFrameDataType): void => {
        this.step = {
            type: 'iframe-view',
            paymentIframeData,
            paysafeDepositSteps,
        };
    };

    public redirectToIframeYaspa = (link: YaspaUrlResponseType['link']): void => {
        this.step = {
            type: 'iframe-view-yaspa',
            url: link,
            yaspaView: 'iframe-view',
        };
    };

    public redirectFromYaspaIframe = (): void => {
        this.step = {
            type: 'iframe-view-yaspa',
            yaspaView: 'loading-view',
            url: '',
        };
    };

    public redirectToFinalView = (transactionId: number): void => {
        this.step = {
            type: 'final-view',
            transactionId,
        };
    };

    public redirectToFailureView = (
        failureType: DepositViewType,
        transactionCallback?: TransactionCallbackType,
        longError?: ErrorMessageType[]
    ): void => {
        this.step = {
            type: 'failure-view',
            failureType,
            transactionCallback,
            longError,
        };
    };
}

export class PaysafeTabState {
    public stepsState: PaysafeDepositSteps;
    public refDepositInput: HTMLInputElement | null;
    public showMoreRingFenceFlag: boolean = false;
    public transactionId: number | undefined = undefined;

    public readonly depositAmount: FormInputState<string, Amount>;
    public readonly minDepositAmount: Amount;
    public readonly accountHelperMail: string;
    public readonly remainingLimitState: RemainingLimitState;
    public readonly languagesState: LanguagesState;
    public readonly websiteBaseUrl: string;
    public readonly paysafePaymentProviderConfig: PaysafePaymentProviderType;
    public readonly errorMessagesState: DepositErrorMessagesState;
    private readonly googleTagManager: GoogleTagManagerState;
    private readonly configComponents: ConfigComponents;
    private readonly operatorFullName: string;
    private readonly trpcClient: TrpcClient;
    private readonly usersState: UsersState;
    private readonly basicDataModel: BasicDataModel;
    private readonly amountPrecision: AmountPrecision;

    public constructor(
        private readonly common: Common,
        private readonly isSignup: boolean
    ) {
        this.stepsState = new PaysafeDepositSteps(this.isSignup);
        this.googleTagManager = GoogleTagManagerState.get(common);
        this.configComponents = ConfigComponents.get(common);
        this.accountHelperMail = this.configComponents.config.accountHelperMail;
        this.minDepositAmount = new Amount(this.configComponents.config.minDepositAmount);
        this.operatorFullName = this.configComponents.config.operatorFullName;
        this.trpcClient = common.trpcClient;
        this.usersState = UsersState.get(common);
        this.remainingLimitState = RemainingLimitState.get(common);
        this.languagesState = LanguagesState.get(common);
        this.basicDataModel = BasicDataModel.get(common);
        this.websiteBaseUrl = common.websiteBaseUrl;
        this.paysafePaymentProviderConfig = PaysafePaymentProviderZod.parse(
            this.configComponents.config.paymentProviders.debitCard
        );
        this.amountPrecision = this.configComponents.precision;
        this.refDepositInput = null;
        this.errorMessagesState = new DepositErrorMessagesState(common, this.languagesState);

        this.depositAmount = FormInputState.new('')
            .map(validateAmountRequire)
            .map(
                validateMinAmountNew(
                    this.minDepositAmount,
                    this.languagesState.getTranslation(
                        'errors.min-deposit',
                        'Minimum deposit amount is {currencySymbol}{minValue}',
                        { currencySymbol: this.basicDataModel.moneySymbol, minValue: this.minDepositAmount.value }
                    )
                )
            )
            .map(
                validateMaxDeposit(
                    () => this.remainingLimitState.remainingLimit ?? null,
                    () => this.basicDataModel.currency,
                    this.languagesState,
                    () => this.basicDataModel.basicDataReady?.depositRestriction,
                    this.accountHelperMail,
                    () => this.remainingLimitState.bindingLimitCalculationType
                )
            );

        makeAutoObservable(this);

        void refreshAccountBasicDataForDeposit(this.common);
    }

    public get depositYaspaPage(): DepositYaspaPageType {
        return {
            isSignup: this.isSignup,
            getAmountInput: () => this.depositAmount,
            handleRedirectToYaspaFrame: (link: YaspaUrlResponseType['link']) =>
                this.stepsState.redirectToIframeYaspa(link),
        };
    }

    public get showBalance(): string | undefined {
        const playableBalance = this.usersState.walletData.valueReady?.playableBalance;

        if (playableBalance !== undefined) {
            return this.basicDataModel.money(new Amount(playableBalance));
        }
    }

    public redirectToInitDeposit = (): void => {
        this.onSetInputToEmpty();
        this.errorMessagesState.clearErrorMessage();
        this.stepsState.redirectToSetMethod();
    };

    /* amount Input start*/
    public onChange = (): void => {
        const formatValue = amountViewToValue(this.depositAmount.value);
        this.depositAmount.setValue(formatValue);
        this.syncDepositBlockingErrors();
    };

    public setAmount = (): void => {
        const amount = parseFloat(this.depositAmount.value);
        if (isNaN(amount) === true || amount === 0) {
            this.depositAmount.setValue('');
            this.syncDepositBlockingErrors();
            return;
        }
        this.depositAmount.setValue(amount.toFixed(2));
        this.syncDepositBlockingErrors();
    };

    public handleAdditionsChange = (amount: Amount): void => {
        this.depositAmount.setValue(amount.value);
        this.depositAmount.setAsVisited();
        this.syncDepositBlockingErrors();
    };

    public onSetInputToEmpty = (): void => {
        this.depositAmount.reset();
    };

    public get hasInputValue(): boolean {
        return this.depositAmount.value !== '';
    }
    /* amount Input end*/

    /* ringFencedFundsFlag start */
    public toggleShowMore = (): void => {
        this.showMoreRingFenceFlag = !this.showMoreRingFenceFlag;
    };

    public get hasRingFencedFunds(): boolean {
        return this.basicDataModel.basicData.valueReady?.ringFencedFunds ?? false;
    }

    /* ringFencedFundsFlag end*/

    private mapIframeData = (
        singleUseToken: string,
        billingInfo: PaysafeBillingInfoSuccess['data'],
        amount: number
    ): PaymentIframeDataType => {
        return {
            customerId: billingInfo.merchantCustomerId,
            data: {
                currency: billingInfo.currency,
                amount: amount,
                singleUseCustomerToken: singleUseToken,
                customer: billingInfo.customer,
                billingAddress: billingInfo.billingAddress,
                merchantRefNum: `${billingInfo.merchantCustomerId}-${singleUseToken}`,
                simulator: 'EXTERNAL',
                environment: this.paysafePaymentProviderConfig.environment,
                buttonColor: '#66cc99',
                imageUrl: 'https://hosted.paysafe.com/checkout/resource/demo-store/images/logo.png',
                companyName: this.operatorFullName,
                holderName: billingInfo.billingAddress.nickName,
                threeDs: {
                    merchantUrl: this.websiteBaseUrl,
                    deviceChannel: 'BROWSER',
                    messageCategory: 'PAYMENT',
                    transactionIntent: 'GOODS_OR_SERVICE_PURCHASE',
                    authenticationPurpose: 'PAYMENT_TRANSACTION',
                },
                displayPaymentMethods: [
                    'card',
                    ...(this.paysafePaymentProviderConfig.applePayEnabled ? ['applePay'] : []),
                ],
                paymentMethodDetails: {
                    card: {
                        accountId:
                            billingInfo.currency === 'GBP'
                                ? this.paysafePaymentProviderConfig.cardAccounts.GBP
                                : this.paysafePaymentProviderConfig.cardAccounts.EUR,
                    },
                    applePay: {
                        accountId:
                            billingInfo.currency === 'GBP'
                                ? this.paysafePaymentProviderConfig.cardAccounts.GBP
                                : this.paysafePaymentProviderConfig.cardAccounts.EUR,
                        label: `${this.operatorFullName}`,
                        requestBillingAddress: true,
                        supportedCountries: ['GB', 'US'],
                        type: 'pay',
                        color: 'black',
                    },
                },
            },
        };
    };

    public paysafeResultCallback =
        (paymentIframeData: PaymentIframeDataType) =>
        async (instance: PaysafeInstanceType | null, error: unknown, result: unknown): Promise<void> => {
            try {
                const date = new Date().toISOString();
                const parsedError = PaysafeError.safeParse(error);
                if (parsedError.success) {
                    console.error('paysafe-iframe-error', parsedError.data);
                    instance?.showFailureScreen(parsedError.data.displayMessage);
                    Logger.captureError(`Paysafe -> SDK error -> ${JSON.stringify(parsedError.data)}`, 'Players');

                    await this.trpcClient.client.paysafeRouter.failedPayment.mutate({
                        amount: this.amountPrecision.newFromOld(paymentIframeData.data.amount).value,
                        currencyCode: paymentIframeData.data.currency,
                        merchantRefNum: paymentIframeData.data.merchantRefNum,
                        error: parsedError.data,
                    });

                    this.stepsState.redirectToFailureView('failWithReceipt', {
                        transactionId: undefined,
                        data: {
                            currency: paymentIframeData.data.currency,
                            amount: paymentIframeData.data.amount,
                            createdAt: date,
                            status: 'failed',
                            orderId: parsedError.data.correlationId,
                        },
                    });
                    return;
                }

                const parsedResult = PaysafeResult.safeParse(result);
                if (parsedResult.success) {
                    const handlePaymentResponse = await this.handlePayment({
                        amount: this.amountPrecision.newFromOld(paymentIframeData.data.amount).value,
                        currencyCode: paymentIframeData.data.currency,
                        description: 'Top-up',
                        keywords: ['Top-up'],
                        paymentHandleToken: parsedResult.data.paymentHandleToken,
                    });

                    if (handlePaymentResponse.responseStatus === 'error') {
                        const error = handlePaymentResponse.data.errors[0];
                        this.handlePaymentError(error, instance, paymentIframeData, date);
                        return;
                    }

                    instance?.showSuccessScreen('Remember to always gamble responsibly');
                    this.stepsState.redirectToFinalView(handlePaymentResponse.response.data.transactionId);
                    return;
                }

                instance?.showFailureScreen(this.errorMessagesState.errors[0]?.message ?? '');
                Logger.captureError(`Paysafe -> Unknown error`, 'Players');
                this.stepsState.redirectToFailureView('serverIssue');
                return;
            } catch (e) {
                console.error('paysafe-iframe-catch-error', e);
                Logger.captureError(`Paysafe -> Iframe catch error -> ${JSON.stringify(e)}`, 'Players');
                this.stepsState.redirectToFailureView('serverIssue');
                instance?.close();
            }
        };

    public paysafeStateCallback = (stage: unknown, expired: unknown): void => {
        const parsedExpired = PaysafeExpired.safeParse(expired);
        if (parsedExpired.success && parsedExpired.data === true) {
            this.stepsState.redirectToFailureView('contactGeneralIssue');
            return;
        }

        const parsedStage = PaysafeStage.safeParse(stage);

        if (parsedStage.success) {
            if (parsedStage.data === 'PAYMENT_HANDLE_NOT_CREATED') {
                this.stepsState.redirectToFailureView('contactGeneralIssue');
                return;
            }
        }

        return;
    };

    public handlePayment = async (
        requestData: CreatePaymentPaysafeType
    ): Promise<createPaymentPaysafe.TresponseType> => {
        return await this.trpcClient.client.paysafeRouter.createPayment.mutate(requestData);
    };

    public submitDepositForm = async (): Promise<void> => {
        this.syncDepositBlockingErrors();
        this.depositAmount.setAsVisited();

        if (this.depositAmount.result.value.type === 'error') {
            console.error('submitDepositForm - Input error');
            return;
        }

        if (isAccountDepositBlocked(this.common, this.hasInputValue)) {
            return;
        }

        const oldFormatAmount = this.amountPrecision.valueOldFormat(this.depositAmount.result.value.data);
        const basicData = this.basicDataModel.basicData.valueReady ?? null;

        const billingInfo = paysafeBillingInfoFieldsValidation(basicData);
        if (billingInfo?.type !== 'ok') {
            console.error('submitDepositForm - No basic user data');
            return;
        }

        try {
            if (this.hasRingFencedFunds === false) {
                await this.usersState.onChangeRingFencedFlag();
            }

            if (this.isSignup) {
                this.googleTagManager.depositedMoney(oldFormatAmount, true);
                this.googleTagManager.gtmSignUpStepFour(oldFormatAmount);
            } else {
                this.googleTagManager.depositedMoney(oldFormatAmount, false);
            }

            const verifyUserResponse = await this.trpcClient.client.paysafeRouter.verifyUser.mutate({
                merchantCustomerId: billingInfo.data.merchantCustomerId.toString(),
                ...billingInfo.data.customer,
            });

            if (verifyUserResponse.responseStatus === 'error') {
                throw new Error();
            }
            const date = new Date().toUTCString();
            const singleUseTokenResponse =
                await this.trpcClient.client.paysafeRouter.createSingleUseCustomerToken.mutate({
                    externalCustomerId: verifyUserResponse.response.data.externalId,
                    body: {
                        merchantRefNum: `${billingInfo.data.merchantCustomerId.toString()}-${date}`,
                        paymentMethod: ['card', 'applePay'],
                    },
                });

            if (singleUseTokenResponse.responseStatus === 'error') {
                throw new Error();
            }

            this.stepsState.redirectToIframeNewCard({
                paymentIframeData: this.mapIframeData(
                    singleUseTokenResponse.response.data.singleUseCustomerToken,
                    billingInfo.data,
                    oldFormatAmount
                ),
                paysafeDepositSteps: this.stepsState,
            });
        } catch (e) {
            this.stepsState.redirectToFailureView('serverIssue');
            throw e;
        }
    };

    private syncDepositBlockingErrors = (): void => {
        syncAccountDepositBlockingErrors(this.common, this.errorMessagesState, this.hasInputValue);
    };

    /* nextButton start (in accounts Tab) */
    public get isButtonDisabled(): boolean {
        if (this.depositAmount.result.value.type === 'error') {
            return true;
        }

        if (isAccountDepositBlocked(this.common, this.hasInputValue)) {
            return true;
        }
        return false;
    }
    /* nextButton end */

    /* used in signUp start */

    public setDepositInputRef = (node: HTMLInputElement | null): void => {
        this.refDepositInput = node;
    };

    private handlePaymentError = (
        error:
            | {
                  code?: string | null;
                  message?: string | null;
                  details?: string[] | null;
                  fieldErrors?: string[] | null;
              }
            | undefined,
        instance: PaysafeInstanceType | null,
        paymentIframeData: PaymentIframeDataType,
        date: string
    ): void => {
        const longMessageErrors = [
            'failed-deposit-restriction',
            'limit-deposit-restriction',
            'limit-reached',
            'backoffice-deposit-restriction',
            'kyc-deposit-restriction',
            'automation-deposit-restriction',
        ];

        const errorCode = error?.code;
        this.errorMessagesState.createErrorMessage(
            error?.code ?? undefined,
            error?.message ?? undefined,
            error?.fieldErrors ?? []
        );

        if (longMessageErrors.includes(errorCode ?? '')) {
            const errorsCopy = [...this.errorMessagesState.errors];

            instance?.showFailureScreen(
                'There was an error with your payment. More information will be shown in the next step.'
            );
            this.stepsState.redirectToFailureView(
                'failWithReceipt',
                {
                    transactionId: undefined,
                    data: {
                        currency: paymentIframeData.data.currency,
                        amount: paymentIframeData.data.amount,
                        createdAt: date,
                        status: 'failed',
                    },
                },
                errorsCopy
            );
        } else {
            instance?.showFailureScreen(this.errorMessagesState.errors[0]?.message ?? '');
            this.stepsState.redirectToFailureView(
                'failWithReceipt',
                {
                    transactionId: undefined,
                    data: {
                        currency: paymentIframeData.data.currency,
                        amount: paymentIframeData.data.amount,
                        createdAt: date,
                        status: 'failed',
                    },
                },
                this.errorMessagesState.errors
            );
        }
    };
}
